/**
 * Phase 20 / Task 20.7 — §80 step 3, preview and validation.
 *
 * `POST …/preview` validates every row and writes nothing live. It is a query
 * here (`staleTime: Infinity`, no refetch on focus) rather than a button the
 * hauler presses: arriving on this step IS the request to validate, and a
 * background refetch would re-validate against a catalog that moved while they
 * read — the screen would change under them.
 *
 * The API returns the first 500 rows of each family (its browser page) and
 * complete counts. The error list can name a row past 500; clicking it then
 * says so and points at `[ Fix errors in file ]`, which carries every row.
 *
 * `[ Proceed with warnings ]` and `[ Import N valid rows ]` both appear only
 * when there are no errors (§80). With warnings present, the first is the
 * hauler's acknowledgement and the second stays disabled until it is given.
 *
 * Beside them, §80's re-import toggle: `[ Update existing ]` (the default, AC-4)
 * or `[ Skip existing — new only ]`, sent as the commit's `mode`.
 *
 * ── Test run (#517) ────────────────────────────────────────────────────────
 * `[ Test run ]` sits beside Import with the same gates, because it runs the
 * same writer (and rolls every row back). DEC-267 caps it at 2,000 rows; over
 * that it is disabled and says why, rather than letting the route refuse it.
 *
 * ── Rates (DEC-256) ────────────────────────────────────────────────────────
 * A file with rate sheets, previewed by a role the commit will refuse for them,
 * says so here and the Import button is disabled — the refusal is shown before
 * the click, in words, rather than after it as a 403 (`rate-tier.ts`).
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';

import { DRY_RUN_MAX_ROWS, requestErrorFile, runPreview } from './api';
import { familyLabel } from './mapping-state';
import { rateFamiliesIn, rateRefusalMessage } from './rate-tier';
import {
  cellTone,
  filterCount,
  filterRows,
  importableRowCount,
  indexFindings,
  previewColumns,
  rowDomId,
  totalErrors,
  totalWarnings,
  type RowFilter,
} from './preview-state';
import type { BatchPreview, CommitMode, FamilyPreview, RowMessage } from './types';
import { ActionButton, Notice, Panel, TONE, TOUCH_MIN, muted, plural } from './ui';

const FILTERS: readonly { readonly id: RowFilter; readonly label: string }[] = [
  { id: 'ALL', label: 'All rows' },
  { id: 'ERROR', label: 'Errors only' },
  { id: 'WARNING', label: 'Warnings only' },
  { id: 'OK', label: 'Valid only' },
];

const PULSE_MS = 1600;

function MessageList(props: {
  readonly family: FamilyPreview;
  readonly items: readonly RowMessage[];
  readonly glyph: string;
  readonly onPick: (rowNumber: number) => void;
}): React.JSX.Element {
  return (
    <ul style={{ padding: 0, margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {props.items.map((m, i) => (
        <li key={`${m.rowNumber.toString()}-${i.toString()}`} style={{ listStyle: 'none' }}>
          <button
            type="button"
            onClick={(): void => props.onPick(m.rowNumber)}
            style={{
              minHeight: TOUCH_MIN,
              width: '100%',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: 'var(--color-text-primary)',
              fontSize: 16,
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            {props.glyph} Row {m.rowNumber.toString()}: {m.message}
          </button>
        </li>
      ))}
    </ul>
  );
}

function FamilySection(props: { readonly family: FamilyPreview }): React.JSX.Element {
  const { family } = props;
  const [filter, setFilter] = React.useState<RowFilter>('ALL');
  const [pulseRow, setPulseRow] = React.useState<number | null>(null);
  const [beyondPage, setBeyondPage] = React.useState<number | null>(null);
  const [showFixes, setShowFixes] = React.useState(false);
  const index = React.useMemo(() => indexFindings(family), [family]);
  const columns = React.useMemo(() => previewColumns(family), [family]);
  const rows = filterRows(family.rows, filter);
  const headingId = `import-preview-${family.entityFamily}`;
  const autoFixTotal = family.autoFixes.reduce((n, a) => n + a.count, 0);

  const pick = (rowNumber: number): void => {
    if (!family.rows.some((r) => r.rowNumber === rowNumber)) {
      setBeyondPage(rowNumber);
      return;
    }
    setBeyondPage(null);
    // The row may be hidden by the current filter — show everything first.
    setFilter('ALL');
    setPulseRow(rowNumber);
    window.setTimeout(() => {
      document
        .getElementById(rowDomId(family.entityFamily, rowNumber))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    window.setTimeout(() => setPulseRow((cur) => (cur === rowNumber ? null : cur)), PULSE_MS);
  };

  return (
    <Panel labelledBy={headingId}>
      <h3 id={headingId} style={{ fontSize: '18px', fontWeight: 600 }}>
        {familyLabel(family.entityFamily)}{' '}
        <span style={muted}>({plural(family.counts.total, 'row')})</span>
      </h3>

      {family.errors.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="error" role="alert">
            ✗ {family.counts.error.toString()}{' '}
            {family.counts.error === 1 ? 'row has errors' : 'rows have errors'} — must fix before
            importing
          </Notice>
          <MessageList family={family} items={family.errors} glyph="✗" onPick={pick} />
        </div>
      )}
      {family.warnings.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="warn">
            ⚠ {family.counts.warning.toString()}{' '}
            {family.counts.warning === 1 ? 'row has warnings' : 'rows have warnings'} — review
            before importing
          </Notice>
          <MessageList family={family} items={family.warnings} glyph="⚠" onPick={pick} />
        </div>
      )}
      {beyondPage !== null && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="warn" role="status">
            Row {beyondPage.toString()} is past the first {plural(family.rows.length, 'row')} shown
            here. Use “Fix errors in file” to get every row with its problems marked.
          </Notice>
        </div>
      )}
      {autoFixTotal > 0 && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="ok">
            ✓ {autoFixTotal.toString()} {autoFixTotal === 1 ? 'item' : 'items'} will be fixed
            automatically on import.{' '}
            <button
              type="button"
              aria-expanded={showFixes}
              onClick={(): void => setShowFixes((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-accent-blue)',
                fontSize: 16,
                cursor: 'pointer',
                minHeight: TOUCH_MIN,
              }}
            >
              {showFixes ? 'Hide details' : 'Show details'}
            </button>
          </Notice>
          {showFixes && (
            <ul style={{ margin: '8px 0 0 24px', fontSize: 16 }}>
              {family.autoFixes.map((a) => (
                <li key={a.summary}>
                  {a.summary} ({a.count.toString()})
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div
        role="group"
        aria-label="Filter rows"
        style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}
      >
        {FILTERS.map((f) => (
          <ActionButton
            key={f.id}
            aria-pressed={filter === f.id}
            variant={filter === f.id ? 'primary' : 'secondary'}
            onClick={(): void => setFilter(f.id)}
          >
            {f.label} ({filterCount(family, f.id).toString()})
          </ActionButton>
        ))}
      </div>

      {family.rows.length < family.counts.total && (
        <p style={{ ...muted, marginTop: 8 }}>
          Showing the first {family.rows.length.toString()} of {plural(family.counts.total, 'row')}.
          The counts above cover every row.
        </p>
      )}

      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 16, minWidth: '100%' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: 14 }}>
              <th style={{ padding: '8px' }}>Row</th>
              <th style={{ padding: '8px' }}>Status</th>
              {columns.map((c) => (
                <th key={c} style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.rowId}
                id={rowDomId(family.entityFamily, row.rowNumber)}
                data-severity={row.severity}
                className={pulseRow === row.rowNumber ? 'import-row-pulse' : undefined}
                style={{ borderTop: '1px solid var(--color-border-primary)' }}
              >
                <td style={{ padding: '8px' }}>{row.rowNumber.toString()}</td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                  {row.severity === 'ERROR'
                    ? '✗ Error'
                    : row.severity === 'WARNING'
                      ? '⚠ Warning'
                      : '✓ Valid'}
                </td>
                {columns.map((c) => {
                  const hit = cellTone(index, row, c);
                  const tone =
                    hit === null
                      ? null
                      : hit.tone === 'ERROR'
                        ? TONE.error
                        : hit.tone === 'WARNING'
                          ? TONE.warn
                          : null;
                  const value = row.values[c] ?? row.mapped[c] ?? '';
                  return (
                    <td
                      key={c}
                      data-cell-tone={hit?.tone}
                      title={hit?.messages.join('\n')}
                      style={{
                        padding: '8px',
                        whiteSpace: 'nowrap',
                        backgroundColor: tone?.bg ?? 'transparent',
                        boxShadow: tone === null ? undefined : `inset 3px 0 0 ${tone.border}`,
                      }}
                    >
                      {hit?.tone === 'ERROR' ? '✗ ' : hit?.tone === 'WARNING' ? '⚠ ' : ''}
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

const MODES: readonly {
  readonly mode: CommitMode;
  readonly label: string;
  readonly detail: string;
}[] = [
  {
    mode: 'UPDATE_ON_MATCH',
    label: 'Update existing',
    detail:
      'A row that matches a record you already have updates it. Empty cells leave its current value alone.',
  },
  {
    mode: 'SKIP_EXISTING',
    label: 'Skip existing — new only',
    detail: 'A row that matches a record you already have is skipped. Only new records are added.',
  },
];

function ModeToggle(props: {
  readonly mode: CommitMode;
  readonly onChange: (mode: CommitMode) => void;
}): React.JSX.Element {
  const current = MODES.find((m) => m.mode === props.mode) ?? MODES[0];
  return (
    <Panel labelledBy="import-mode-heading">
      <h3 id="import-mode-heading" style={{ fontSize: 16, fontWeight: 600 }}>
        When a row matches a record you already have
      </h3>
      <div
        role="group"
        aria-labelledby="import-mode-heading"
        style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}
      >
        {MODES.map((m) => (
          <ActionButton
            key={m.mode}
            aria-pressed={props.mode === m.mode}
            variant={props.mode === m.mode ? 'primary' : 'secondary'}
            onClick={(): void => props.onChange(m.mode)}
          >
            {m.label}
          </ActionButton>
        ))}
      </div>
      <p style={{ ...muted, marginTop: 8 }}>{current?.detail}</p>
    </Panel>
  );
}

export function PreviewStep(props: {
  readonly batchId: string;
  readonly mode: CommitMode;
  readonly onModeChange: (mode: CommitMode) => void;
  readonly onBack: () => void;
  readonly onImport: () => void;
  /** Commit as a test run: every row written, then rolled back. */
  readonly onTestRun: () => void;
  /** False for a role the commit refuses rate templates to (DEC-256). */
  readonly canImportRates: boolean;
  /** §80: after fixing the file in Excel the hauler re-uploads — back to step 1. */
  readonly onRestart: () => void;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const query = useQuery<BatchPreview>({
    queryKey: ['imports', props.batchId, 'preview'],
    queryFn: () => runPreview(apiOpts, props.batchId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [errorFileBusy, setErrorFileBusy] = React.useState(false);
  const [errorFileError, setErrorFileError] = React.useState<string | null>(null);

  if (query.isPending) {
    return <Panel>Checking every row… nothing is saved during this step.</Panel>;
  }
  if (query.isError) {
    return (
      <Panel>
        <Notice tone="error" role="alert">
          {query.error.message}
        </Notice>
        <div style={{ marginTop: 16 }}>
          <ActionButton onClick={props.onBack}>Back to column mapping</ActionButton>
        </div>
      </Panel>
    );
  }

  const preview = query.data;
  const errors = totalErrors(preview.families);
  const warnings = totalWarnings(preview.families);
  const importable = importableRowCount(preview.families);
  const rateFamilies = props.canImportRates
    ? []
    : rateFamiliesIn(preview.families.filter((f) => f.counts.total > 0).map((f) => f.entityFamily));
  const rateBlocked = rateFamilies.length > 0;
  const canImport = preview.canProceed && !rateBlocked && (warnings === 0 || acknowledged);
  // DEC-267 counts STAGED rows, which is every row in the preview, errors included.
  const stagedRows = preview.families.reduce((n, f) => n + f.counts.total, 0);
  const overTestCap = stagedRows > DRY_RUN_MAX_ROWS;

  const downloadErrorFile = async (): Promise<void> => {
    setErrorFileBusy(true);
    setErrorFileError(null);
    try {
      const file = await requestErrorFile(apiOpts, props.batchId);
      window.open(file.downloadUrl, '_blank', 'noopener');
    } catch (err: unknown) {
      setErrorFileError(err instanceof Error ? err.message : 'Could not build the error file.');
    } finally {
      setErrorFileBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel labelledBy="import-preview-heading">
        <h2 id="import-preview-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
          Step 3 of 4 — Check before importing
        </h2>
        <p style={{ ...muted, marginTop: 8 }}>
          Every row has been checked. Nothing has been saved yet.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {rateBlocked && (
            <Notice tone="error" role="alert">
              {rateRefusalMessage(rateFamilies)}
            </Notice>
          )}
          {preview.batchProblems.map((p) => (
            <Notice key={p} tone="error" role="alert">
              {p}
            </Notice>
          ))}
          {errors > 0 && (
            <Notice tone="error">
              ✗ {errors.toString()} {errors === 1 ? 'row has' : 'rows have'} errors — fix them in
              your file and upload it again.
            </Notice>
          )}
          {warnings > 0 && (
            <Notice tone="warn">
              ⚠ {warnings.toString()} {warnings === 1 ? 'row has' : 'rows have'} warnings — they can
              be imported, but review them first.
            </Notice>
          )}
          {errors === 0 && warnings === 0 && preview.batchProblems.length === 0 && (
            <Notice tone="ok">✓ Every row is ready to import.</Notice>
          )}
        </div>
      </Panel>

      {preview.families.map((f) => (
        <FamilySection key={f.entityFamily} family={f} />
      ))}

      {preview.canProceed && <ModeToggle mode={props.mode} onChange={props.onModeChange} />}

      {preview.canProceed && overTestCap && (
        <p style={muted}>
          A test run is limited to {DRY_RUN_MAX_ROWS.toLocaleString('en-US')} rows, and this file
          has {stagedRows.toLocaleString('en-US')}. Split the file to test it, or import it directly
          — an import can be undone afterwards.
        </p>
      )}
      {errorFileError !== null && (
        <Notice tone="error" role="alert">
          {errorFileError}
        </Notice>
      )}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {(errors > 0 || warnings > 0) && (
          <ActionButton
            disabled={errorFileBusy}
            onClick={(): void => {
              void downloadErrorFile();
            }}
          >
            {errorFileBusy ? 'Building the file…' : 'Fix errors in file'}
          </ActionButton>
        )}
        {errors > 0 && <ActionButton onClick={props.onRestart}>Upload the fixed file</ActionButton>}
        {preview.canProceed && warnings > 0 && (
          <ActionButton
            aria-pressed={acknowledged}
            variant={acknowledged ? 'primary' : 'secondary'}
            onClick={(): void => setAcknowledged(true)}
          >
            {acknowledged ? '✓ Proceeding with warnings' : 'Proceed with warnings'}
          </ActionButton>
        )}
        {preview.canProceed && (
          <ActionButton disabled={!canImport || overTestCap} onClick={props.onTestRun}>
            Test run (nothing is saved)
          </ActionButton>
        )}
        {preview.canProceed && (
          <ActionButton variant="primary" disabled={!canImport} onClick={props.onImport}>
            Import {importable.toString()} valid {importable === 1 ? 'row' : 'rows'}
          </ActionButton>
        )}
        <ActionButton variant="ghost" onClick={props.onBack}>
          Back to column mapping
        </ActionButton>
      </div>
    </div>
  );
}
